import React, { useRef } from 'react';
import { Card, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { useEnteredView } from '@fiverr-private/hooks';
import { SellerEntityTypes } from '@fiverr-private/upmarket_common';
import { useResizableRef, useTemplatesData } from '../../hooks';
import { BiData, reportEventsApi } from '../../biEvents';
import { HandlePaymentRequest, LegalDocsConfigurations } from '../../types';
import { InitialInteractionButton } from '../InitialInteractionButton/InitialInteractionButton';
import { LegalDocumentsTitleDescription } from './LegalDocumentsTitleDescription';

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates.empty_state';

export interface DocumentsListEmptyStateProps {
    sellerId: number;
    sellerName: string;
    configurations?: LegalDocsConfigurations;
    totalTemplates: number;
    activeTemplates: number;
    handlePayment: HandlePaymentRequest;
    hideTitleDescription?: boolean;
    biData: BiData;
    sellerEntityType?: SellerEntityTypes;
}

export const DocumentsListEmptyState = ({
    sellerId,
    sellerName,
    configurations,
    totalTemplates,
    activeTemplates,
    handlePayment,
    hideTitleDescription,
    biData,
    sellerEntityType,
}: DocumentsListEmptyStateProps) => {
    const { templatesQuery } = useTemplatesData();
    const { ref, compact } = useResizableRef();
    const totalTemplatesCount = templatesQuery?.data ? templatesQuery.data.length : totalTemplates;

    const observableRef = useRef<HTMLDivElement | null>(null);

    const combinedRef = (node: HTMLDivElement | null) => {
        ref(node);
        observableRef.current = node;
    };

    useEnteredView(observableRef, {
        threshold: 1,
        onEntered: () => {
            reportEventsApi.viewedNoLegalDocs({ pageName: biData.pageName });
        },
    });

    return (
        <Card borderRadius="lg" ref={combinedRef}>
            <Stack
                paddingLeft={compact ? '4' : '5'}
                paddingRight={compact ? '2' : '3'}
                paddingY={compact ? '1.5' : '2'}
                justifyContent="spaceBetween"
                alignItems="center"
                height={compact ? '44px' : '48px'}
                as="span"
                whiteSpace="nowrap"
                overflow="hidden"
            >
                <Stack overflow="hidden" gap="4">
                    <Text
                        size={
                            compact
                                ? 'b_sm'
                                : {
                                      default: 'b_sm',
                                      sm: 'b_md',
                                  }
                        }
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        flexShrink={0}
                    >
                        <I18n k={`${TRANSLATION_PREFIX}.title`} />
                    </Text>
                    {!hideTitleDescription && (
                        <LegalDocumentsTitleDescription totalTemplates={totalTemplates} compact={compact} />
                    )}
                </Stack>
                <Stack>
                    <InitialInteractionButton
                        totalTemplates={totalTemplatesCount}
                        activeTemplates={activeTemplates}
                        sellerName={sellerName}
                        sellerId={sellerId}
                        configurations={configurations}
                        handlePayment={handlePayment}
                        biData={biData}
                        sellerEntityType={sellerEntityType}
                    />
                </Stack>
            </Stack>
        </Card>
    );
};
